import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { Y_AXIS_WIDTH } from "~/feature/chart/constants";

import {
  DOT_SIZE,
  MOBILE_DOT_SIZE,
  MOBILE_EDGE_GAP,
  TOOLTIP_MAX_WIDTH,
  TOOLTIP_OFFSET_X,
  TOOLTIP_TOP,
} from "./constants";

const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

export const Root = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$leftPercent" && prop !== "$isFlipped",
})<{ $leftPercent: number; $isFlipped: boolean }>(
  ({ $leftPercent, $isFlipped, theme }) => ({
    position: "absolute",
    top: TOOLTIP_TOP,
    left: `calc(${Y_AXIS_WIDTH}px + (100% - ${Y_AXIS_WIDTH}px) * ${$leftPercent / 100})`,
    transform: $isFlipped
      ? `translateX(calc(-100% - ${TOOLTIP_OFFSET_X}px))`
      : `translateX(${TOOLTIP_OFFSET_X}px)`,
    gap: theme.spacing(1),
    pointerEvents: "none",
    zIndex: theme.zIndex.tooltip,

    // Follows the tapped bucket like on desktop, but clamped inside the
    // plot — no side of a phone screen fits an unclamped cursor-anchored box.
    [theme.breakpoints.down("sm")]: {
      left: `clamp(${Y_AXIS_WIDTH}px, calc(${Y_AXIS_WIDTH}px + (100% - ${Y_AXIS_WIDTH}px) * ${$leftPercent / 100} + ${TOOLTIP_OFFSET_X}px), calc(100% - ${TOOLTIP_MAX_WIDTH + MOBILE_EDGE_GAP}px))`,
      transform: "none",
    },
  })
);

export const TooltipBox = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
  padding: theme.spacing(1, 1.25),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],

  [theme.breakpoints.down("sm")]: {
    maxWidth: TOOLTIP_MAX_WIDTH,
    gap: theme.spacing(0.375),
    padding: theme.spacing(0.5, 0.75),
  },
}));

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  color: theme.palette.text.disabled,

  [theme.breakpoints.down("sm")]: {
    fontSize: 9,
  },
}));

export const Row = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  justifyContent: "space-between",

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
  },
}));

export const RowInfo = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(0.75),
  minWidth: 0,

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.5),
  },
}));

export const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>(({ $color, theme }) => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  flexShrink: 0,
  marginTop: theme.spacing(0.5),
  borderRadius: "50%",
  backgroundColor: $color,

  [theme.breakpoints.down("sm")]: {
    width: MOBILE_DOT_SIZE,
    height: MOBILE_DOT_SIZE,
    marginTop: theme.spacing(0.25),
  },
}));

export const LabelBlock = styled(Box)(({ theme }) => ({
  minWidth: 0,
  display: "flex",
  alignItems: "baseline",
  gap: theme.spacing(0.5),

  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

export const Label = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isConsensus",
})<{ $isConsensus: boolean }>(({ $isConsensus, theme }) => ({
  fontSize: 12,
  ...ellipsis,
  color: $isConsensus ? theme.palette.gold.dark : theme.palette.text.secondary,
  fontWeight: $isConsensus ? 600 : 400,

  [theme.breakpoints.down("sm")]: {
    fontSize: 10,
  },
}));

export const Sublabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isConsensus",
})<{ $isConsensus: boolean }>(({ $isConsensus, theme }) => ({
  fontSize: 12,
  ...ellipsis,
  color: $isConsensus ? theme.palette.gold.dark : theme.palette.text.secondary,
  fontWeight: $isConsensus ? 600 : 400,

  "&::before": {
    content: $isConsensus ? '""' : '"- "',
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 9,
    color: $isConsensus ? theme.palette.gold.dark : theme.palette.text.disabled,

    "&::before": {
      content: "none",
    },
  },
}));

export const Price = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isConsensus",
})<{ $isConsensus: boolean }>(({ $isConsensus, theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  color: $isConsensus ? theme.palette.gold.dark : theme.palette.text.primary,

  [theme.breakpoints.down("sm")]: {
    fontSize: 10,
  },
}));
